import { useState, useEffect } from "react";
import { Search, Globe, Shield, Smartphone, Zap, AlertTriangle, CheckCircle, XCircle, ArrowLeft, RefreshCw, Clock, ExternalLink, FileText, Image, Link2, Code, Server, Eye, Download, History, Trash2, Tag, Layers, Key, Share2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import ToolSEOContent from "@/components/ToolSEOContent";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function SiteAuditPage() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [auditHistory, setAuditHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [activeTab, setActiveTab] = useState("audit");
  const [sharing, setSharing] = useState(false);

  // Load audit history on mount
  useEffect(() => {
    loadAuditHistory();
  }, []);

  const loadAuditHistory = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/site-audit/history`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAuditHistory(data.audits || []);
      }
    } catch (err) {
      console.log("No audit history");
    } finally {
      setLoadingHistory(false);
    }
  };

  const runAudit = async () => {
    let auditUrl = url.trim();
    if (!auditUrl) {
      toast.error("Please enter a URL");
      return;
    }
    
    if (!auditUrl.startsWith("http://") && !auditUrl.startsWith("https://")) {
      auditUrl = "https://" + auditUrl;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await fetch(`${API_BASE}/api/site-audit/advanced`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',
        body: JSON.stringify({ url: auditUrl, save_history: true }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setResults(data);
      toast.success("Audit completed!");
      loadAuditHistory(); // Refresh history
    } catch (err) {
      setError(err.message || "Failed to audit site");
      toast.error(err.message || "Failed to audit site");
    } finally {
      setLoading(false);
    }
  };

  const loadPreviousAudit = async (auditId) => {
    try {
      const response = await fetch(`${API_BASE}/api/site-audit/history/${auditId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setResults(data);
        setActiveTab("audit");
        toast.success("Loaded previous audit");
      }
    } catch (err) {
      toast.error("Failed to load audit");
    }
  };

  const deleteAudit = async (auditId, e) => {
    e.stopPropagation();
    try {
      await fetch(`${API_BASE}/api/site-audit/history/${auditId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setAuditHistory(prev => prev.filter(a => a.id !== auditId));
      toast.success("Audit deleted");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const shareReport = async () => {
    if (!results) return;
    
    setSharing(true);
    try {
      const response = await axios.post(`${API_BASE}/api/share/create`, {
        url: results.url || url,
        score: results.overall_score || 0,
        title: `SEO Audit: ${results.url || url}`,
        issues_found: results.issues?.length || 0,
        recommendations: results.recommendations?.length || 5
      });
      
      if (response.data.success) {
        navigator.clipboard.writeText(response.data.share_url);
        toast.success("Share link copied to clipboard!");
        navigate(`/report/${response.data.share_id}`);
      }
    } catch (error) {
      toast.error("Failed to create shareable report");
    } finally {
      setSharing(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "bg-green-50 border-green-200";
    if (score >= 50) return "bg-yellow-50 border-yellow-200";
    return "bg-red-50 border-red-200";
  };

  const getSeverityColor = (severity) => {
    if (severity === "high") return "bg-red-50 border-red-200 text-red-700";
    if (severity === "medium") return "bg-yellow-50 border-yellow-200 text-yellow-700";
    return "bg-blue-50 border-blue-200 text-blue-700";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link to="/tools/seo" className="inline-flex items-center text-gray-600 hover:text-violet-600 mb-2">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to SEO Tools
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Complete Site Audit</h1>
          <p className="text-gray-600">100+ SEO checks • Performance • Security • Mobile • Technical Analysis</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <Search className="w-4 h-4" /> New Audit
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="w-4 h-4" /> Audit History ({auditHistory.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="audit" className="mt-6">
            {/* Input Section */}
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  <div className="relative flex-1">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="Enter website URL (e.g., example.com)"
                      className="pl-10 h-12 text-lg"
                      onKeyPress={(e) => e.key === "Enter" && runAudit()}
                      data-testid="site-audit-url-input"
                    />
                  </div>
                  <Button
                    onClick={runAudit}
                    disabled={loading}
                    className="h-12 px-8 bg-gradient-to-r from-violet-600 to-purple-600"
                    data-testid="site-audit-run-btn"
                  >
                    {loading ? (
                      <><RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Analyzing...</>
                    ) : (
                      <><Search className="w-5 h-5 mr-2" /> Run Full Audit</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Error */}
            {error && (
              <Card className="mb-6 border-red-200 bg-red-50">
                <CardContent className="pt-6 flex items-center gap-3 text-red-700">
                  <XCircle className="w-6 h-6" />
                  <div>
                    <p className="font-medium">Audit Failed</p>
                    <p className="text-sm">{error}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Loading */}
            {loading && (
              <Card className="mb-6">
                <CardContent className="py-12 text-center">
                  <RefreshCw className="w-12 h-12 text-violet-600 animate-spin mx-auto mb-4" />
                  <p className="text-lg font-medium">Running comprehensive audit...</p>
                  <p className="text-gray-500">Checking 100+ SEO factors</p>
                </CardContent>
              </Card>
            )}

            {/* Results */}
            {results && (
              <div className="space-y-6">
                {/* Domain Card */}
                <Card className="bg-gradient-to-r from-violet-600 to-purple-600 text-white">
                  <CardContent className="py-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Globe className="w-10 h-10" />
                        <div>
                          <h2 className="text-2xl font-bold">{results.domain}</h2>
                          <p className="text-violet-200">{results.url}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex gap-2 mb-2">
                          <Button 
                            onClick={shareReport} 
                            disabled={sharing}
                            className="bg-white/20 hover:bg-white/30 text-white"
                            size="sm"
                          >
                            <Share2 className="w-4 h-4 mr-1" />
                            {sharing ? "Creating..." : "Share Report"}
                          </Button>
                        </div>
                        <p className="text-violet-200 text-sm">Audited</p>
                        <p className="font-medium">{new Date(results.analyzed_at || Date.now()).toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Score Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {[
                    { label: "Overall", score: results.overall_score, icon: Eye },
                    { label: "SEO", score: results.seo_score, icon: Search },
                    { label: "Performance", score: results.performance_score, icon: Zap },
                    { label: "Security", score: results.security_score, icon: Shield },
                    { label: "Mobile", score: results.mobile_score, icon: Smartphone },
                    { label: "Technical", score: results.technical_score, icon: Code },
                  ].map((item) => (
                    <Card key={item.label} className={`${getScoreBg(item.score)} border-2`}>
                      <CardContent className="pt-4 text-center">
                        <item.icon className="w-5 h-5 mx-auto mb-1 text-gray-500" />
                        <p className="text-xs text-gray-600 uppercase font-medium">{item.label}</p>
                        <p className={`text-3xl font-bold ${getScoreColor(item.score)}`}>{item.score}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* AI-Powered Quick Recommendations */}
                <Card className="border-2 border-violet-200 bg-gradient-to-r from-violet-50 to-purple-50">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-violet-700">
                      <Zap className="w-5 h-5" /> Quick Wins - Fix These First
                    </CardTitle>
                    <p className="text-sm text-gray-600">AI-generated recommendations based on your audit</p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(() => {
                      const recommendations = [];
                      
                      // Generate recommendations based on scores
                      if (results.seo_score < 80) {
                        if (results.page_meta?.title?.length < 30 || results.page_meta?.title?.length > 60) {
                          recommendations.push({
                            priority: 'high',
                            text: `Optimize title tag length (currently ${results.page_meta?.title?.length || 0} chars, ideal: 50-60)`,
                            impact: '+5 SEO Score'
                          });
                        }
                        if (!results.page_meta?.meta_description?.value || results.page_meta?.meta_description?.length < 120) {
                          recommendations.push({
                            priority: 'high',
                            text: 'Add/improve meta description (150-160 characters recommended)',
                            impact: '+3 SEO Score'
                          });
                        }
                      }
                      
                      if (results.security_score < 90) {
                        recommendations.push({
                          priority: 'medium',
                          text: 'Enable HTTPS and security headers for better security',
                          impact: '+10 Security Score'
                        });
                      }
                      
                      if (results.performance_score < 80) {
                        recommendations.push({
                          priority: 'medium',
                          text: 'Optimize images and enable compression for faster loading',
                          impact: '+8 Performance Score'
                        });
                      }
                      
                      if (results.mobile_score < 85) {
                        recommendations.push({
                          priority: 'medium',
                          text: 'Improve mobile responsiveness and touch targets',
                          impact: '+7 Mobile Score'
                        });
                      }
                      
                      if (results.content?.word_count < 300) {
                        recommendations.push({
                          priority: 'low',
                          text: `Add more content (currently ${results.content?.word_count || 0} words, aim for 1000+)`,
                          impact: '+5 SEO Score'
                        });
                      }
                      
                      // Default if score is good
                      if (recommendations.length === 0) {
                        recommendations.push({
                          priority: 'success',
                          text: 'Great job! Your site is well-optimized. Keep monitoring for changes.',
                          impact: 'Maintain rankings'
                        });
                      }
                      
                      return recommendations.slice(0, 5).map((rec, idx) => (
                        <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg ${
                          rec.priority === 'high' ? 'bg-red-50 border border-red-200' :
                          rec.priority === 'medium' ? 'bg-yellow-50 border border-yellow-200' :
                          rec.priority === 'success' ? 'bg-green-50 border border-green-200' :
                          'bg-gray-50 border border-gray-200'
                        }`}>
                          {rec.priority === 'high' && <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />}
                          {rec.priority === 'medium' && <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />}
                          {rec.priority === 'low' && <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />}
                          {rec.priority === 'success' && <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />}
                          <div className="flex-1">
                            <p className="text-gray-800 font-medium">{rec.text}</p>
                            <p className="text-sm text-gray-500">Expected impact: {rec.impact}</p>
                          </div>
                        </div>
                      ));
                    })()}
                  </CardContent>
                </Card>

                {/* Page Meta Section */}
                {results.page_meta && (
                  <Card className="border-2 border-blue-200">
                    <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-indigo-50">
                      <CardTitle className="flex items-center gap-2 text-blue-700">
                        <FileText className="w-5 h-5" /> Page Meta Tags
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4 space-y-4">
                      {/* Title */}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-700">Title Tag</span>
                          <Badge className={results.page_meta.title?.status === 'good' ? 'bg-green-100 text-green-700' : results.page_meta.title?.status === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>
                            {results.page_meta.title?.length || 0} chars
                          </Badge>
                        </div>
                        <p className="text-gray-800 font-medium">{results.page_meta.title?.value || 'No title found'}</p>
                        <div className="flex gap-2 mt-2">
                          {results.page_meta.title?.has_keyword && <Badge variant="outline" className="text-green-600">Has Keyword</Badge>}
                          {results.page_meta.title?.has_brand && <Badge variant="outline" className="text-blue-600">Has Brand</Badge>}
                        </div>
                      </div>
                      
                      {/* Meta Description */}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-700">Meta Description</span>
                          <Badge className={results.page_meta.meta_description?.status === 'good' ? 'bg-green-100 text-green-700' : results.page_meta.meta_description?.status === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>
                            {results.page_meta.meta_description?.length || 0} chars
                          </Badge>
                        </div>
                        <p className="text-gray-600 text-sm">{results.page_meta.meta_description?.value || 'No meta description found'}</p>
                        {results.page_meta.meta_description?.has_cta && (
                          <Badge variant="outline" className="text-green-600 mt-2">Has CTA</Badge>
                        )}
                      </div>
                      
                      {/* Other Meta Tags Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="p-3 bg-gray-50 rounded-lg text-center">
                          <p className="text-xs text-gray-500">Robots</p>
                          <p className="font-medium text-sm">{results.page_meta.robots?.value || 'index, follow'}</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg text-center">
                          <p className="text-xs text-gray-500">Charset</p>
                          <p className="font-medium text-sm">{results.page_meta.charset?.value || 'Not Set'}</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg text-center">
                          <p className="text-xs text-gray-500">Favicon</p>
                          {results.page_meta.favicon?.present ? (
                            <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500 mx-auto" />
                          )}
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg text-center">
                          <p className="text-xs text-gray-500">Author</p>
                          <p className="font-medium text-sm truncate">{results.page_meta.author?.value || 'Not Set'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Platform & Keywords Info */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Platform Detection */}
                  {results.platform && (
                    <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-indigo-700">
                          <Layers className="w-5 h-5" /> Platform Detected
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-3xl font-bold text-indigo-700">{results.platform.platform}</p>
                            {results.platform.version && (
                              <p className="text-sm text-indigo-500">Version: {results.platform.version}</p>
                            )}
                          </div>
                          <Badge className={
                            results.platform.confidence === 'high' ? 'bg-green-500' :
                            results.platform.confidence === 'medium' ? 'bg-yellow-500' :
                            'bg-gray-500'
                          }>
                            {results.platform.confidence} confidence
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                          {results.platform.platform === 'WordPress' && 'Popular CMS used by 40%+ of websites'}
                          {results.platform.platform === 'Shopify' && 'Leading e-commerce platform'}
                          {results.platform.platform === 'Wix' && 'Website builder platform'}
                          {results.platform.platform === 'Next.js' && 'React framework for production'}
                          {results.platform.platform === 'Custom/Static HTML' && 'Custom built website'}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Main Keywords */}
                  {results.main_keywords && (
                    <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-emerald-700">
                          <Key className="w-5 h-5" /> Main Keywords
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="mb-3">
                          <p className="text-sm text-gray-600">Primary Keyword:</p>
                          <p className="text-xl font-bold text-emerald-700">{results.main_keywords.primary_keyword}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {results.main_keywords.keywords?.slice(0, 8).map((kw, idx) => (
                            <Badge 
                              key={idx} 
                              variant="outline" 
                              className="bg-white border-emerald-300 text-emerald-700"
                            >
                              {kw}
                            </Badge>
                          ))}
                        </div>
                        {results.main_keywords.h1_keywords?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-emerald-200">
                            <p className="text-xs text-gray-500 mb-1">From H1 Tags:</p>
                            <p className="text-sm text-emerald-600 line-clamp-1">{results.main_keywords.h1_keywords[0]}</p>
                          </div>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          {results.main_keywords.from_meta ? '✓ Meta keywords found' : '⚠ No meta keywords tag'}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Page Info */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Meta Info */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-violet-600" /> Page Meta
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Title ({results.page_meta?.title?.length || 0} chars)</p>
                        <p className="font-medium bg-gray-50 p-3 rounded">{results.page_meta?.title?.value || "No title found"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Meta Description ({results.page_meta?.meta_description?.length || 0} chars)</p>
                        <p className="text-sm bg-gray-50 p-3 rounded">{results.page_meta?.meta_description?.value || "No description found"}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-3 rounded">
                          <p className="text-sm text-gray-500">Keywords</p>
                          <p className="font-medium">{results.main_keywords?.keyword_count || results.keywords?.length || 0} found</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded">
                          <p className="text-sm text-gray-500">Language</p>
                          <p className="font-medium">{results.technical_seo?.language?.value || results.language || "Not set"}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Content Stats */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Code className="w-5 h-5 text-violet-600" /> Content Analysis
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                          <FileText className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                          <p className="text-2xl font-bold">{results.content?.word_count || results.on_page_seo?.word_count?.value || 0}</p>
                          <p className="text-sm text-gray-500">Words</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                          <Image className="w-6 h-6 mx-auto mb-2 text-green-500" />
                          <p className="text-2xl font-bold">{results.on_page_seo?.images?.total || 0}</p>
                          <p className="text-sm text-gray-500">Images</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                          <Link2 className="w-6 h-6 mx-auto mb-2 text-purple-500" />
                          <p className="text-2xl font-bold">{results.links?.total || 0}</p>
                          <p className="text-sm text-gray-500">Links</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                          <ExternalLink className="w-6 h-6 mx-auto mb-2 text-orange-500" />
                          <p className="text-2xl font-bold">{results.links?.external || 0}</p>
                          <p className="text-sm text-gray-500">External Links</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Headings Structure */}
                {results.headings && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Heading Structure</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-4 flex-wrap">
                        {['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map(tag => (
                          <div key={tag} className="bg-gray-50 px-4 py-2 rounded-lg">
                            <span className="font-mono font-bold text-violet-600">{tag.toUpperCase()}</span>
                            <span className="ml-2 text-gray-700">{results.headings[tag] || 0}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Technical Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Server className="w-5 h-5 text-violet-600" /> Technical Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {[
                        { label: "Status", value: results.technical?.status_code || results.status_code || "200" },
                        { label: "Response Time", value: `${(results.technical?.response_time || results.response_time || 0).toFixed(2)}s` },
                        { label: "Page Size", value: `${((results.technical?.page_size || results.page_size || 0) / 1024).toFixed(1)} KB` },
                        { label: "HTTPS", value: results.url?.startsWith("https") ? "Yes ✓" : "No ✗" },
                        { label: "Robots.txt", value: results.robots_txt ? "Found ✓" : "Missing" },
                        { label: "Sitemap", value: results.sitemap ? "Found ✓" : "Missing" },
                      ].map(item => (
                        <div key={item.label} className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-500">{item.label}</p>
                          <p className="font-bold">{item.value}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Issues */}
                {results.issues && results.issues.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-orange-500" />
                        Issues Found ({results.issues.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {results.issues.map((issue, idx) => (
                        <div key={idx} className={`p-4 rounded-lg border ${getSeverityColor(issue.severity)}`}>
                          <div className="flex items-start gap-3">
                            {issue.severity === "high" ? (
                              <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            ) : issue.severity === "medium" ? (
                              <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            ) : (
                              <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            )}
                            <div>
                              <p className="font-medium">{issue.title || issue.issue}</p>
                              <p className="text-sm opacity-80 mt-1">{issue.description || issue.recommendation}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Recommendations */}
                {results.recommendations && results.recommendations.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-5 h-5" /> Recommendations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {results.recommendations.map((rec, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                            <span>{typeof rec === 'string' ? rec : (rec.title || rec.description || JSON.stringify(rec))}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Your Audit History</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingHistory ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                  </div>
                ) : auditHistory.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No audits yet. Run your first audit!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {auditHistory.map((audit) => (
                      <div
                        key={audit.id}
                        onClick={() => loadPreviousAudit(audit.id)}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <Globe className="w-8 h-8 text-violet-600" />
                          <div>
                            <p className="font-medium">{audit.domain}</p>
                            <p className="text-sm text-gray-500">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {new Date(audit.analyzed_at || audit.audited_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className={`text-2xl font-bold ${getScoreColor(audit.overall_score)}`}>
                            {audit.overall_score}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => deleteAudit(audit.id, e)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <ToolSEOContent toolId="site-audit" />
    </div>
  );
}
